/**
 * AuditFindings - Non-conformance & observation tracker with CAPA workflow
 * 
 * Features:
 *   - List all findings with status filters
 *   - Assign findings to users for corrective action
 *   - CAPA workflow: Open → Assigned → In Progress → Resolved → Verified → Closed
 *   - Risk classification (likelihood × consequence)
 *   - Due dates and SLA tracking
 *   - Comment thread per finding
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, Search, CheckCircle2, Clock,
  User, CalendarDays, ArrowLeft, Send, FileText,
  Shield, Loader2, MoreHorizontal, ChevronRight, MessageSquare, X,
  ChevronsUpDown, ListFilter, RotateCcw, Activity, ClipboardCheck,
  Timer, TrendingUp, Hash, CalendarClock,
} from 'lucide-react';
import { Card, CardContent } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { Badge } from '@shared/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@shared/components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '@shared/components/ui/popover';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';
import { ENV } from '@shared/config/env';

const STATUS_CONFIG = {
  open:            { label: 'Open',        color: 'bg-red-500/10 text-red-700 border-red-300',    icon: AlertTriangle },
  action_assigned: { label: 'Assigned',    color: 'bg-orange-500/10 text-orange-700 border-orange-300', icon: User },
  in_progress:     { label: 'In Progress', color: 'bg-amber-500/10 text-amber-700 border-amber-300',  icon: Clock },
  resolved:        { label: 'Resolved',    color: 'bg-blue-500/10 text-blue-700 border-blue-300',    icon: CheckCircle2 },
  verified:        { label: 'Verified',    color: 'bg-green-500/10 text-green-700 border-green-300',  icon: Shield },
  closed:          { label: 'Closed',      color: 'bg-gray-500/10 text-gray-600 border-gray-300',    icon: CheckCircle2 },
};

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-600 text-white' },
  high:     { label: 'High',     color: 'bg-red-500 text-white' },
  medium:   { label: 'Medium',   color: 'bg-amber-500 text-white' },
  low:      { label: 'Low',      color: 'bg-blue-500 text-white' },
};

const FINDING_TYPES = {
  observation:              { label: 'Observation',     color: 'text-amber-600' },
  opportunity_for_improvement: { label: 'OFI',          color: 'text-blue-600' },
  non_conformity:           { label: 'Non-Conformity',  color: 'text-red-600' },
  major_non_conformity:     { label: 'Major NC',        color: 'text-red-800' },
};

const ACTION_STATUS_CONFIG = {
  assigned:              { label: 'Assigned',              color: 'bg-orange-500/10 text-orange-700 border-orange-300', next: 'in_progress' },
  in_progress:           { label: 'In Progress',           color: 'bg-amber-500/10 text-amber-700 border-amber-300',  next: 'completed' },
  completed:             { label: 'Completed',             color: 'bg-blue-500/10 text-blue-700 border-blue-300',    next: 'verification_pending' },
  verification_pending:  { label: 'Pending Verification',  color: 'bg-purple-500/10 text-purple-700 border-purple-300', next: 'verified' },
  verified:              { label: 'Verified',              color: 'bg-green-500/10 text-green-700 border-green-300',  next: 'closed' },
  rejected:              { label: 'Rejected',              color: 'bg-red-500/10 text-red-700 border-red-300',       next: 'in_progress' },
  closed:                { label: 'Closed',                color: 'bg-gray-500/10 text-gray-600 border-gray-300',    next: null },
};

// ─── Pending Extension Reviews (collapsible banner) ─────────────────
function PendingExtensionReviews({ user }) {
  const [requests, setRequests] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [activeReqId, setActiveReqId] = useState(null);
  const [extReviewNotes, setExtReviewNotes] = useState('');
  const [extSaving, setExtSaving] = useState(false);
  const [extLoading, setExtLoading] = useState(true);

  const loadRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('action_extension_requests')
        .select(`
          *,
          requester:users!action_extension_requests_requested_by_fkey(first_name, surname, email),
          audit_actions!inner(
            id, action_title, assigned_by, assigned_to, due_date, status,
            audit_findings ( finding_title, audits ( audit_reference, audit_title ) )
          )
        `)
        .eq('status', 'pending')
        .eq('audit_actions.assigned_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      console.error('Error loading extension requests:', err);
    } finally {
      setExtLoading(false);
    }
  };

  useEffect(() => { loadRequests(); }, [user?.id]);

  const handleReview = async (request, decision) => {
    try {
      setExtSaving(true);
      const { error } = await supabase
        .from('action_extension_requests')
        .update({
          status: decision,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: extReviewNotes || null,
        })
        .eq('id', request.id);
      if (error) throw error;

      if (decision === 'approved') {
        await supabase
          .from('audit_actions')
          .update({ due_date: request.requested_due_date })
          .eq('id', request.action_id);
      }

      // Email the requester
      try {
        const requesterEmail = request.requester?.email;
        if (requesterEmail) {
          const { data: authData } = await supabase.auth.getSession();
          const currentUserName = [user?.first_name, user?.surname].filter(Boolean).join(' ') || user?.email || 'Manager';
          fetch(ENV.SUPABASE.edgeFn('notify-extension-request'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authData.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              type: decision,
              to_email: requesterEmail,
              to_name: [request.requester.first_name, request.requester.surname].filter(Boolean).join(' ') || requesterEmail,
              from_name: currentUserName,
              action_title: request.audit_actions?.action_title || 'Corrective Action',
              original_due_date: request.original_due_date,
              requested_due_date: request.requested_due_date,
              review_notes: extReviewNotes || null,
            }),
          }).catch(err => console.warn('Extension notification failed:', err));
        }
      } catch { /* non-blocking */ }

      toast({
        title: decision === 'approved' ? 'Extension Approved' : 'Extension Rejected',
        description: decision === 'approved'
          ? `Due date updated to ${new Date(request.requested_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
          : 'The extension request has been rejected',
      });
      setActiveReqId(null);
      setExtReviewNotes('');
      loadRequests();
    } catch (err) {
      console.error('Error reviewing extension:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setExtSaving(false);
    }
  };

  if (extLoading || requests.length === 0) return null;

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 overflow-hidden">
      {/* Slim clickable banner */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <CalendarClock className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-semibold text-foreground">Pending Extension Requests</span>
          <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0">{requests.length}</Badge>
        </div>
        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {/* Expandable content */}
      {expanded && (
        <div className="border-t border-blue-200 dark:border-blue-800 px-4 py-3 space-y-2">
          {requests.map(req => {
            const requesterName = req.requester
              ? [req.requester.first_name, req.requester.surname].filter(Boolean).join(' ') || req.requester.email
              : 'Unknown';
            const action = req.audit_actions;
            const audit = action?.audit_findings?.audits;
            const isActive = activeReqId === req.id;
            const origDate = req.original_due_date
              ? new Date(req.original_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
              : '—';
            const reqDate = new Date(req.requested_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

            return (
              <div key={req.id} className="rounded-md border bg-white dark:bg-slate-900 overflow-hidden">
                {/* Compact row */}
                <div
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  onClick={() => { setActiveReqId(isActive ? null : req.id); setExtReviewNotes(''); }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{action?.action_title || 'Corrective Action'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {requesterName} · {origDate} → <span className="font-semibold text-blue-600 dark:text-blue-400">{reqDate}</span>
                      {audit?.audit_reference && <span className="ml-1.5 font-mono text-[10px] bg-muted/60 px-1 py-0.5 rounded">{audit.audit_reference}</span>}
                    </p>
                  </div>
                  {!isActive ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={extSaving}
                        onClick={() => handleReview(req, 'approved')}
                      >
                        {extSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        disabled={extSaving}
                        onClick={() => handleReview(req, 'rejected')}
                      >
                        <X className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>

                {/* Expanded detail */}
                {isActive && (
                  <div className="px-3 pb-3 space-y-2 border-t bg-slate-50/50 dark:bg-slate-800/30" onClick={e => e.stopPropagation()}>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <div className="text-xs">
                        <span className="text-muted-foreground">Current due: </span>
                        <span className="font-medium">{req.original_due_date ? new Date(req.original_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Requested: </span>
                        <span className="font-semibold text-blue-700 dark:text-blue-400">{new Date(req.requested_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground italic">&ldquo;{req.reason}&rdquo;</p>
                    <Textarea
                      value={extReviewNotes}
                      onChange={e => setExtReviewNotes(e.target.value)}
                      placeholder="Review notes (optional)..."
                      rows={2}
                      className="text-xs resize-none"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AuditFindings({ user }) {
  const [findings, setFindings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignFinding, setAssignFinding] = useState(null);
  const [users, setUsers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [actionDescription, setActionDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueDays, setDueDays] = useState('14'); // severity-based default
  const [saving, setSaving] = useState(false);
  const [detailActions, setDetailActions] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [closingNotes, setClosingNotes] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [extensionRequests, setExtensionRequests] = useState({}); // action_id → latest request
  const [reviewingExtension, setReviewingExtension] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [savingReview, setSavingReview] = useState(false);

  useEffect(() => {
    loadFindings();
    loadUsers();
  }, []);

  // Load extension requests when detail actions change
  useEffect(() => {
    if (detailActions.length > 0) {
      loadExtensionRequests(detailActions.map(a => a.id));
    } else {
      setExtensionRequests({});
    }
  }, [detailActions]);

  const loadFindings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('audit_findings')
        .select(`
          *,
          audits ( audit_title, audit_reference ),
          audit_actions ( id, action_description, assigned_to, status, due_date )
        `)
        .order('identified_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      setFindings(data || []);
    } catch (error) {
      console.error('Error loading findings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const STAFF_ROLES = ['admin', 'depot_manager', 'approver', 'employee'];

  const loadUsers = async () => {
    const { data } = await supabase
      .from('users')
      .select('id, first_name, surname, email, role')
      .in('role', STAFF_ROLES)
      .order('first_name');
    let list = (data || []).map(u => ({
      ...u,
      full_name: [u.first_name, u.surname].filter(Boolean).join(' ') || null,
    }));
    const currentName = [user?.first_name, user?.surname].filter(Boolean).join(' ') || user?.full_name || 'Current User';
    if (user?.id && !list.find(u => u.id === user.id)) {
      list = [{ id: user.id, full_name: currentName, email: user.email, role: user.role || 'admin' }, ...list];
    }
    setUsers(list);
  };

  const getUserName = (userId) => {
    const u = users.find(x => x.id === userId);
    return u ? (u.full_name || u.email) : 'Unknown';
  };

  const openAssignDialog = (finding) => {
    setAssignFinding(finding);
    // Auto-set timeframe based on severity
    const days = finding.severity === 'critical' ? '3' : finding.severity === 'high' ? '7' : '14';
    setDueDays(days);
    setDueDate(new Date(Date.now() + parseInt(days) * 86400000).toISOString().slice(0, 10));
    setAssigneeId('');
    setActionDescription('');
    setShowAssignDialog(true);
  };

  const handleStatusChange = async (finding, newStatus) => {
    try {
      const updates = { status: newStatus };
      if (newStatus === 'closed') {
        updates.closed_at = new Date().toISOString();
        updates.closed_by = user.id;
      }
      await supabase.from('audit_findings').update(updates).eq('id', finding.id);
      toast({ title: 'Status Updated', description: `Finding marked as ${STATUS_CONFIG[newStatus]?.label}` });
      loadFindings();
      if (selectedFinding?.id === finding.id) {
        setSelectedFinding({ ...finding, ...updates });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleActionStatusChange = async (action, newStatus) => {
    try {
      const updates = { status: newStatus };
      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
        updates.completed_by = user.id;
        updates.completion_notes = actionNotes || null;
      }
      if (newStatus === 'verified') {
        updates.verified_at = new Date().toISOString();
        updates.verified_by = user.id;
        updates.verification_notes = actionNotes || null;
      }
      await supabase.from('audit_actions').update(updates).eq('id', action.id);
      toast({ title: 'Action Updated', description: `Action marked as ${ACTION_STATUS_CONFIG[newStatus]?.label}` });
      setActionNotes('');
      if (selectedFinding) loadDetailActions(selectedFinding.id);
      loadFindings();
    } catch (error) {
      console.error('Error updating action:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const loadDetailActions = async (findingId) => {
    setDetailLoading(true);
    const { data } = await supabase
      .from('audit_actions')
      .select('*')
      .eq('finding_id', findingId)
      .order('assigned_at', { ascending: false });
    setDetailActions(data || []);
    setDetailLoading(false);
  };

  // ── Extension requests for detail view ──────────────────────
  const loadExtensionRequests = async (actionIds) => {
    if (!actionIds?.length) { setExtensionRequests({}); return; }
    try {
      const { data, error } = await supabase
        .from('action_extension_requests')
        .select('*, users:requested_by(first_name, surname, email)')
        .in('action_id', actionIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      // Group by action_id — keep the latest per action
      const map = {};
      (data || []).forEach(req => {
        if (!map[req.action_id]) map[req.action_id] = req;
      });
      setExtensionRequests(map);
    } catch (err) {
      console.error('Error loading extension requests:', err);
    }
  };

  const handleReviewExtension = async (request, decision) => {
    try {
      setSavingReview(true);
      // Update the extension request
      const { error } = await supabase
        .from('action_extension_requests')
        .update({
          status: decision,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes || null,
        })
        .eq('id', request.id);
      if (error) throw error;

      // If approved, also update the action's due_date
      if (decision === 'approved') {
        const { error: actionErr } = await supabase
          .from('audit_actions')
          .update({ due_date: request.requested_due_date })
          .eq('id', request.action_id);
        if (actionErr) throw actionErr;
      }

      // Fire-and-forget: notify the assignee via email
      try {
        const requester = request.users; // joined from query
        const requesterEmail = requester?.email;
        if (requesterEmail) {
          const { data: authData } = await supabase.auth.getSession();
          const action = detailActions.find(a => a.id === request.action_id);
          const currentUserName = [user?.first_name, user?.surname].filter(Boolean).join(' ') || user?.email || 'Manager';
          fetch(ENV.SUPABASE.edgeFn('notify-extension-request'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authData.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              type: decision,
              to_email: requesterEmail,
              to_name: [requester.first_name, requester.surname].filter(Boolean).join(' ') || requesterEmail,
              from_name: currentUserName,
              action_title: action?.action_title || 'Corrective Action',
              finding_title: selectedFinding?.finding_title || '',
              audit_reference: selectedFinding?.audits?.audit_reference || '',
              original_due_date: request.original_due_date,
              requested_due_date: request.requested_due_date,
              review_notes: reviewNotes || null,
            }),
          }).catch(err => console.warn('Extension notification email failed (non-blocking):', err));
        }
      } catch (notifyErr) {
        console.warn('Could not send extension notification email:', notifyErr);
      }

      toast({
        title: decision === 'approved' ? 'Extension Approved' : 'Extension Rejected',
        description: decision === 'approved'
          ? 'Due date has been updated'
          : 'The assignee has been notified',
      });

      setReviewingExtension(null);
      setReviewNotes('');
      // Reload data
      if (selectedFinding) {
        await loadDetailActions(selectedFinding.id);
      }
      loadFindings();
    } catch (err) {
      console.error('Error reviewing extension:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingReview(false);
    }
  };

  const openFindingDetail = async (finding) => {
    setSelectedFinding(finding);
    setClosingNotes('');
    setActionNotes('');
    setReviewingExtension(null);
    setReviewNotes('');
    await loadDetailActions(finding.id);
  };

  const handleCloseFinding = async () => {
    if (!selectedFinding) return;
    try {
      setSaving(true);
      await supabase.from('audit_findings').update({
        status: 'closed',
        closed_at: new Date().toISOString(),
        closed_by: user.id,
      }).eq('id', selectedFinding.id);

      await supabase.from('audit_actions').update({
        status: 'closed',
        completed_at: new Date().toISOString(),
        completion_notes: closingNotes || 'Closed with finding',
      }).eq('finding_id', selectedFinding.id).neq('status', 'closed');

      toast({ title: 'Finding Closed', description: 'Finding and all actions have been closed' });
      setSelectedFinding(null);
      loadFindings();
    } catch (error) {
      console.error('Error closing finding:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async () => {
    if (!assigneeId || !actionDescription.trim()) {
      toast({ title: 'Required', description: 'Select an assignee and describe the action', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);

      // Create action
      const defaultDue = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
      await supabase.from('audit_actions').insert({
        finding_id: assignFinding.id,
        audit_id: assignFinding.audit_id,
        action_type: 'corrective_action',
        action_title: `CAPA: ${assignFinding.finding_title || assignFinding.finding_description?.slice(0, 80) || 'Finding'}`,
        action_description: actionDescription,
        assigned_to: assigneeId,
        assigned_by: user.id,
        status: 'assigned',
        due_date: dueDate || defaultDue,
        priority: assignFinding.severity === 'critical' ? 'critical' : 
                  assignFinding.severity === 'high' ? 'high' : 'medium',
      });

      // Update finding status
      await supabase.from('audit_findings')
        .update({ status: 'action_assigned' })
        .eq('id', assignFinding.id);

      // Fire-and-forget: notify assignee via email
      try {
        const assignee = users.find(u => u.id === assigneeId);
        if (assignee?.email) {
          const { data: authData } = await supabase.auth.getSession();
          const defaultDueStr = dueDate || new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
          fetch(ENV.SUPABASE.edgeFn('notify-action-assigned'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authData.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              assigned_to_email: assignee.email,
              assigned_to_name: assignee.full_name || assignee.email,
              assigned_by_name: user.full_name || user.email || 'A team member',
              finding_title: assignFinding.finding_title || assignFinding.finding_description?.slice(0, 80) || 'Finding',
              finding_type: assignFinding.finding_type || 'non_conformity',
              severity: assignFinding.severity || 'medium',
              action_description: actionDescription,
              due_date: defaultDueStr,
              audit_reference: assignFinding.audits?.audit_reference || '',
            }),
          }).catch(err => console.warn('Notification email failed (non-blocking):', err));
        }
      } catch (notifyErr) {
        console.warn('Could not send notification email:', notifyErr);
      }

      toast({ title: 'Action Assigned', description: 'Corrective action created and assigned' });
      setShowAssignDialog(false);
      setAssignFinding(null);
      setAssigneeId('');
      setActionDescription('');
      setDueDate('');
      setDueDays('14');
      loadFindings();
    } catch (error) {
      console.error('Error assigning:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const filtered = findings.filter(f => {
    const matchesSearch = !search ||
      f.finding_description?.toLowerCase().includes(search.toLowerCase()) ||
      f.finding_title?.toLowerCase().includes(search.toLowerCase()) ||
      f.audits?.audit_reference?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || f.status === statusFilter;
    const matchesType = typeFilter === 'all' || f.finding_type === typeFilter;
    const matchesUser = userFilter === 'all' || (f.audit_actions || []).some(a => a.assigned_to === userFilter);
    return matchesSearch && matchesStatus && matchesType && matchesUser;
  });

  // Stats
  const openCount = findings.filter(f => f.status === 'open').length;
  const assignedCount = findings.filter(f => f.status === 'action_assigned').length;
  const overdueCount = findings.filter(f =>
    f.status === 'open' && f.identified_at &&
    (new Date() - new Date(f.identified_at)) / (1000 * 60 * 60 * 24) > 14
  ).length;

  // ── Finding Detail View ──
  if (selectedFinding) {
    const f = selectedFinding;
    const statusCfg = STATUS_CONFIG[f.status] || STATUS_CONFIG.open;
    const severityCfg = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.medium;
    const typeCfg = FINDING_TYPES[f.finding_type] || FINDING_TYPES.observation;
    const isClosed = f.status === 'closed';

    return (
      <div className="p-5 space-y-5 max-w-3xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => setSelectedFinding(null)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Findings
        </button>

        {/* Finding Header Card */}
        <div className="rounded-xl border-2 bg-white dark:bg-card shadow-sm overflow-hidden">
          <div className={`px-5 py-3 flex items-center justify-between ${
            f.finding_type === 'non_conformity' || f.finding_type === 'major_non_conformity'
              ? 'bg-red-50 dark:bg-red-950/20 border-b border-red-200 dark:border-red-800/40'
              : 'bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-800/40'
          }`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`text-[10px] ${severityCfg.color}`}>{severityCfg.label}</Badge>
              <span className={`text-xs font-semibold ${typeCfg.color}`}>{typeCfg.label}</span>
              <Badge variant="outline" className={`text-[10px] ${statusCfg.color}`}>{statusCfg.label}</Badge>
            </div>
            {f.audits?.audit_reference && (
              <span className="font-mono text-xs text-muted-foreground bg-white/60 dark:bg-black/20 px-2 py-0.5 rounded">
                {f.audits.audit_reference}
              </span>
            )}
          </div>
          <div className="p-5 space-y-3">
            <h2 className="font-bold text-base">{f.finding_title || 'Finding'}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{f.finding_description}</p>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2 border-t border-dashed">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                Identified: {f.identified_at ? new Date(f.identified_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
              </span>
              {f.audits?.audit_title && (
                <span className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  {f.audits.audit_title}
                </span>
              )}
              {f.location && (
                <span>Location: {f.location}</span>
              )}
            </div>
          </div>
        </div>

        {/* Status Workflow Buttons */}
        {!isClosed && (
          <div className="rounded-xl border bg-white dark:bg-card shadow-sm p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Update Finding Status</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                const StatusIcon = cfg.icon;
                const isCurrent = f.status === key;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (!isCurrent) {
                        handleStatusChange(f, key);
                        setSelectedFinding({ ...f, status: key });
                      }
                    }}
                    disabled={isCurrent}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${
                      isCurrent
                        ? `${cfg.color} border-current ring-2 ring-offset-1`
                        : 'bg-white dark:bg-card border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400 hover:shadow-sm'
                    }`}
                  >
                    <StatusIcon className="h-3.5 w-3.5" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions Section */}
        <div className="rounded-xl border bg-white dark:bg-card shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between bg-muted/20">
            <h3 className="text-sm font-semibold">Corrective Actions ({detailActions.length})</h3>
            {!isClosed && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => openAssignDialog(f)}
              >
                <User className="h-3 w-3 mr-1.5" /> Add Action
              </Button>
            )}
          </div>

          {detailLoading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : detailActions.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No corrective actions yet</p>
              {!isClosed && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => openAssignDialog(f)}
                >
                  <User className="h-3.5 w-3.5 mr-1.5" /> Assign Action
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {detailActions.map(action => {
                const aCfg = ACTION_STATUS_CONFIG[action.status] || ACTION_STATUS_CONFIG.assigned;
                const isOverdue = action.due_date && new Date(action.due_date) < new Date() && action.status !== 'closed' && action.status !== 'verified';
                const assigneeName = getUserName(action.assigned_to);

                return (
                  <div key={action.id} className="p-4 sm:p-5 space-y-3">
                    {/* Action header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">{action.action_title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{action.action_description}</p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold shrink-0 ${aCfg.color}`}>
                        {aCfg.label}
                      </span>
                    </div>

                    {/* Action meta */}
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <User className="h-3 w-3" /> {assigneeName}
                      </span>
                      <span className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
                        <CalendarDays className="h-3 w-3" />
                        Due: {action.due_date ? new Date(action.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}
                        {isOverdue && ' (OVERDUE)'}
                      </span>
                      {action.priority && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          action.priority === 'critical' ? 'bg-red-100 text-red-700' :
                          action.priority === 'high' ? 'bg-red-50 text-red-600' :
                          action.priority === 'medium' ? 'bg-amber-50 text-amber-600' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {action.priority.toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Completion / verification notes */}
                    {action.completion_notes && (
                      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 px-3 py-2">
                        <span className="text-[10px] font-semibold text-blue-600 uppercase">Completion Notes</span>
                        <p className="text-xs text-blue-800 dark:text-blue-300 mt-0.5">{action.completion_notes}</p>
                      </div>
                    )}
                    {action.verification_notes && (
                      <div className="rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 px-3 py-2">
                        <span className="text-[10px] font-semibold text-green-600 uppercase">Verification Notes</span>
                        <p className="text-xs text-green-800 dark:text-green-300 mt-0.5">{action.verification_notes}</p>
                      </div>
                    )}

                    {/* Extension request review (for assigner) */}
                    {(() => {
                      const extReq = extensionRequests[action.id];
                      if (!extReq) return null;
                      const requester = extReq.users;
                      const requesterName = requester
                        ? [requester.first_name, requester.surname].filter(Boolean).join(' ') || requester.email
                        : 'Unknown';

                      return (
                        <div className={`rounded-lg border p-3 space-y-2 ${
                          extReq.status === 'pending'
                            ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/40 ring-1 ring-blue-300 dark:ring-blue-700'
                            : extReq.status === 'approved'
                              ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40'
                              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/40'
                        }`}>
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                              <CalendarClock className="h-3.5 w-3.5" />
                              Extension Request from {requesterName}
                            </p>
                            <Badge variant="outline" className={`text-[10px] ${
                              extReq.status === 'pending'
                                ? 'bg-blue-100 text-blue-700 border-blue-300'
                                : extReq.status === 'approved'
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                  : 'bg-red-100 text-red-700 border-red-300'
                            }`}>
                              {extReq.status === 'pending' ? 'Pending Review' : extReq.status === 'approved' ? 'Approved' : 'Rejected'}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Original due:</span>{' '}
                              <span className="font-medium">
                                {extReq.original_due_date
                                  ? new Date(extReq.original_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                                  : '—'}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Requested:</span>{' '}
                              <span className="font-semibold text-blue-700 dark:text-blue-400">
                                {new Date(extReq.requested_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          </div>

                          <p className="text-xs text-muted-foreground italic">&ldquo;{extReq.reason}&rdquo;</p>

                          {/* Review actions (only for pending) */}
                          {extReq.status === 'pending' && (
                            <div className="pt-2 border-t border-blue-200 dark:border-blue-700 space-y-2">
                              {reviewingExtension === extReq.id ? (
                                <>
                                  <Textarea
                                    value={reviewNotes}
                                    onChange={e => setReviewNotes(e.target.value)}
                                    placeholder="Review notes (optional)..."
                                    rows={2}
                                    className="text-xs resize-none"
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                      disabled={savingReview}
                                      onClick={() => handleReviewExtension(extReq, 'approved')}
                                    >
                                      {savingReview ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                      disabled={savingReview}
                                      onClick={() => handleReviewExtension(extReq, 'rejected')}
                                    >
                                      <X className="h-3 w-3 mr-1" /> Reject
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs"
                                      onClick={() => { setReviewingExtension(null); setReviewNotes(''); }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </>
                              ) : (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                    onClick={() => setReviewingExtension(extReq.id)}
                                  >
                                    <CalendarClock className="h-3 w-3 mr-1.5" /> Review Request
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Show review notes if already reviewed */}
                          {extReq.review_notes && extReq.status !== 'pending' && (
                            <p className="text-xs mt-1">
                              <span className="font-semibold">Review notes:</span> {extReq.review_notes}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Progress action for this action */}
                    {action.status !== 'closed' && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
                        <div className="flex-1 w-full">
                          <Textarea
                            value={actionNotes}
                            onChange={e => setActionNotes(e.target.value)}
                            placeholder={action.status === 'completed' || action.status === 'verification_pending'
                              ? 'Add verification notes (optional)...'
                              : 'Add completion notes (optional)...'}
                            rows={2}
                            className="text-xs resize-none"
                          />
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {aCfg.next && (
                            <Button
                              size="sm"
                              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleActionStatusChange(action, aCfg.next)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1.5" />
                              {aCfg.nextLabel}
                            </Button>
                          )}
                          {action.status !== 'rejected' && action.status !== 'verified' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleActionStatusChange(action, 'rejected')}
                            >
                              <X className="h-3 w-3 mr-1.5" /> Reject
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Close Finding */}
        {!isClosed && (
          <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-card p-5 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" /> Close Finding
            </h3>
            <p className="text-xs text-muted-foreground">
              Closing this finding will also close all associated corrective actions.
            </p>
            <Textarea
              value={closingNotes}
              onChange={e => setClosingNotes(e.target.value)}
              placeholder="Add closing notes (optional)..."
              rows={2}
              className="text-sm resize-none"
            />
            <Button
              onClick={handleCloseFinding}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Close Finding & All Actions
            </Button>
          </div>
        )}

        {isClosed && (
          <div className="rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/40 p-4 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800 dark:text-green-400">Finding Closed</p>
              <p className="text-xs text-green-700 dark:text-green-500">
                Closed {f.closed_at ? new Date(f.closed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-r from-background via-muted/30 to-background p-5">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500" />
        <div className="relative flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <ClipboardCheck className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-foreground">Audit Actions</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {findings.length} finding{findings.length !== 1 ? 's' : ''} · {openCount} open · {overdueCount} overdue
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs">
              <Activity className="h-3 w-3 mr-1" />
              Live
            </Badge>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Open', count: openCount, icon: AlertTriangle, gradient: 'from-red-500 to-rose-600', bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
          { label: 'Assigned', count: assignedCount, icon: User, gradient: 'from-orange-500 to-amber-600', bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800' },
          { label: 'Overdue', count: overdueCount, icon: Timer,
            gradient: overdueCount > 0 ? 'from-red-600 to-red-700' : 'from-emerald-500 to-green-600',
            bg: overdueCount > 0 ? 'bg-red-50 dark:bg-red-950/30' : 'bg-emerald-50 dark:bg-emerald-950/30',
            text: overdueCount > 0 ? 'text-red-800 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400',
            border: overdueCount > 0 ? 'border-red-200 dark:border-red-800' : 'border-emerald-200 dark:border-emerald-800' },
          { label: 'Closed', count: findings.filter(f => f.status === 'closed').length, icon: CheckCircle2, gradient: 'from-emerald-500 to-green-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className={`relative overflow-hidden border ${s.border} ${s.bg} transition-all hover:shadow-md`}>
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${s.gradient}`} />
              <div className="p-4 pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-3xl font-bold ${s.text}`}>{s.count}</p>
                    <p className="text-xs font-medium text-muted-foreground mt-0.5">{s.label}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${s.bg}`}>
                    <Icon className={`h-5 w-5 ${s.text} opacity-60`} />
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Pending Extension Requests (collapsible banner for managers) */}
      <PendingExtensionReviews user={user} />

      {/* Filters */}
      <Card className="p-0 overflow-hidden border shadow-sm">
        {/* Search row */}
        <div className="p-3 border-b bg-gradient-to-r from-muted/40 to-muted/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground\" />
            <Input
              placeholder="Search by title, description, or audit reference..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-background shadow-sm"
            />
          </div>
        </div>
        {/* Filter controls row */}
        <div className="px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground mr-1">
            <ListFilter className="h-4 w-4" />
            <span>Filters</span>
            {(statusFilter !== 'all' || typeFilter !== 'all' || userFilter !== 'all') && (
              <Badge variant="secondary" className="h-5 min-w-[20px] px-1.5 text-[10px] font-bold rounded-full">
                {[statusFilter !== 'all', typeFilter !== 'all', userFilter !== 'all'].filter(Boolean).length}
              </Badge>
            )}
          </div>

          <div className="h-5 w-px bg-border hidden sm:block" />

          {/* Status */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground hidden sm:inline">Status</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[140px] text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Type */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground hidden sm:inline">Type</span>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[150px] text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(FINDING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Assigned To — searchable combobox */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground hidden sm:inline">Assigned&nbsp;To</span>
            <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="h-8 w-[180px] justify-between font-normal text-sm px-3">
                  <span className="truncate">
                    {userFilter === 'all' ? 'All Users' : getUserName(userFilter)}
                  </span>
                  {userFilter !== 'all' ? (
                    <X
                      className="ml-1 h-3 w-3 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); setUserFilter('all'); setUserSearch(''); }}
                    />
                  ) : (
                    <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className="h-8 pl-8 text-sm"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-[240px] overflow-y-auto p-1">
                  <button
                    className={`w-full text-left px-2 py-1.5 rounded-sm text-sm hover:bg-accent cursor-pointer ${
                      userFilter === 'all' ? 'bg-accent font-medium' : ''
                    }`}
                    onClick={() => { setUserFilter('all'); setUserSearch(''); setUserDropdownOpen(false); }}
                  >
                    All Users
                  </button>
                  {(() => {
                    const term = userSearch.toLowerCase().trim();
                    const matched = term
                      ? users.filter(u =>
                          (u.full_name || '').toLowerCase().includes(term) ||
                          (u.email || '').toLowerCase().includes(term)
                        )
                      : users;
                    const capped = matched.slice(0, 7);
                    const remaining = matched.length - capped.length;
                    return (
                      <>
                        {capped.map(u => (
                          <button
                            key={u.id}
                            className={`w-full text-left px-2 py-1.5 rounded-sm text-sm hover:bg-accent cursor-pointer flex items-center gap-2 ${
                              userFilter === u.id ? 'bg-accent font-medium' : ''
                            }`}
                            onClick={() => { setUserFilter(u.id); setUserSearch(''); setUserDropdownOpen(false); }}
                          >
                            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate">{u.full_name || u.email}</span>
                          </button>
                        ))}
                        {capped.length === 0 && (
                          <div className="px-2 py-3 text-sm text-muted-foreground text-center">No users match "{userSearch}"</div>
                        )}
                        {remaining > 0 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground text-center border-t mt-1 pt-1.5">
                            + {remaining} more — type to narrow
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Clear all — only shown when filters are active */}
          {(statusFilter !== 'all' || typeFilter !== 'all' || userFilter !== 'all') && (
            <>
              <div className="h-5 w-px bg-border hidden sm:block" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground hover:text-foreground gap-1"
                onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setUserFilter('all'); setUserSearch(''); }}
              >
                <RotateCcw className="h-3 w-3" />
                Clear filters
              </Button>
            </>
          )}

          {/* Results count — pushed to the right */}
          <div className="ml-auto text-xs text-muted-foreground">
            {filtered.length} of {findings.length} results
          </div>
        </div>
      </Card>

      {/* Findings List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <Card key={i} className="overflow-hidden">
              <div className="flex">
                <div className="w-1 bg-muted animate-pulse" />
                <div className="flex-1 p-4 space-y-3">
                  <div className="h-4 bg-muted/40 rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-muted/30 rounded animate-pulse w-1/2" />
                  <div className="h-3 bg-muted/20 rounded animate-pulse w-1/3" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-16 text-center border-dashed">
          <div className="inline-flex p-4 rounded-full bg-green-50 dark:bg-green-950/30 mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-500/50" />
          </div>
          <p className="text-muted-foreground font-medium">No findings match your filters</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your search or filter criteria</p>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {filtered.map(finding => {
            const statusCfg = STATUS_CONFIG[finding.status] || STATUS_CONFIG.open;
            const StatusIcon = statusCfg.icon;
            const severityCfg = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.medium;
            const typeCfg = FINDING_TYPES[finding.finding_type] || FINDING_TYPES.observation;
            const actions = finding.audit_actions || [];
            const daysSince = finding.identified_at
              ? Math.floor((new Date() - new Date(finding.identified_at)) / (1000 * 60 * 60 * 24))
              : 0;
            const severityBorder = {
              critical: 'bg-red-600',
              high: 'bg-red-500',
              medium: 'bg-amber-500',
              low: 'bg-blue-500',
            }[finding.severity] || 'bg-amber-500';
            const isOverdue = finding.status === 'open' && daysSince > 14;

            return (
              <Card
                key={finding.id}
                className={`group overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer border ${
                  isOverdue ? 'border-red-300 dark:border-red-800 bg-red-50/30 dark:bg-red-950/10' : 'hover:border-amber-400/60'
                }`}
                onClick={() => openFindingDetail(finding)}
              >
                <div className="flex">
                  {/* Severity color strip */}
                  <div className={`w-1.5 ${severityBorder} shrink-0 rounded-l-lg`} />

                  <CardContent className="flex-1 p-4">
                    <div className="flex items-start gap-3">
                      {/* Status icon */}
                      <div className={`p-2 rounded-lg ${statusCfg.color} shrink-0 ring-1 ring-inset ring-black/5`}>
                        <StatusIcon className="h-4 w-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Badges row */}
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <Badge className={`text-[10px] font-semibold shadow-sm ${severityCfg.color}`}>{severityCfg.label}</Badge>
                          <span className={`text-xs font-semibold ${typeCfg.color}`}>{typeCfg.label}</span>
                          <Badge variant="outline" className={`text-[10px] font-medium ${statusCfg.color}`}>{statusCfg.label}</Badge>
                          {isOverdue && (
                            <Badge className="text-[10px] bg-red-600 text-white animate-pulse shadow-sm">OVERDUE</Badge>
                          )}
                        </div>

                        {/* Title & description */}
                        <p className="text-sm font-semibold line-clamp-1 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">
                          {finding.finding_title || 'Finding'}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{finding.finding_description}</p>

                        {/* Meta row */}
                        <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                          {finding.audits?.audit_reference && (
                            <span className="inline-flex items-center gap-1 font-mono text-[11px] bg-muted/60 px-1.5 py-0.5 rounded text-muted-foreground">
                              <Hash className="h-3 w-3" />
                              {finding.audits.audit_reference}
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${
                            isOverdue ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400' : 'text-muted-foreground'
                          }`}>
                            <CalendarDays className="h-3 w-3" />
                            {daysSince}d ago
                          </span>
                          {actions.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
                              <User className="h-3 w-3" />
                              {actions.length} action{actions.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {finding.status === 'open' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAssignDialog(finding);
                            }}
                          >
                            <User className="h-3 w-3 mr-1" /> Assign
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Assign Action Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={o => { if (!o) { setShowAssignDialog(false); setAssignFinding(null); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Corrective Action</DialogTitle>
            <DialogDescription>
              {assignFinding?.finding_description?.slice(0, 100)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Assign To *</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger><SelectValue placeholder="Select person..." /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Action Required *</Label>
              <Textarea
                value={actionDescription}
                onChange={e => setActionDescription(e.target.value)}
                placeholder="Describe the corrective action required..."
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Timeframe *</Label>
              <Select value={dueDays} onValueChange={(v) => {
                setDueDays(v);
                if (v !== 'custom') {
                  setDueDate(new Date(Date.now() + parseInt(v) * 86400000).toISOString().slice(0, 10));
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Select timeframe..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 Days — Critical</SelectItem>
                  <SelectItem value="7">7 Days — High Priority</SelectItem>
                  <SelectItem value="14">14 Days — Standard</SelectItem>
                  <SelectItem value="30">30 Days — Extended</SelectItem>
                  <SelectItem value="60">60 Days — Long Term</SelectItem>
                  <SelectItem value="custom">Custom Date</SelectItem>
                </SelectContent>
              </Select>
              {dueDays === 'custom' ? (
                <Input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="mt-1.5"
                />
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Due: {dueDate ? new Date(dueDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Select timeframe'}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={saving || !dueDate}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <User className="h-4 w-4 mr-2" />}
              Assign Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
